import React, { useEffect, useState, useCallback } from "react";
import { useNotify } from "react-admin";
import { useClub } from "../club/ClubContext";
import { httpClient } from "../httpClient";
import {
  Box, Typography, Card, CardContent, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Switch, FormControlLabel,
  IconButton, Tooltip, Chip, CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WorkIcon from "@mui/icons-material/Work";

interface JobType {
  id: number;
  club_id: number;
  name: string;
  description?: string | null;
  active: boolean;
  created_at?: string;
}

function JobTypeDialog({
  open, onClose, initial, clubId, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial?: JobType | null;
  clubId: number;
  onSaved: () => void;
}) {
  const notify = useNotify();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setDescription(initial?.description ?? "");
      setActive(initial?.active ?? true);
    }
  }, [open, initial]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (initial) {
        await httpClient(`/api/events/job-types/${initial.id}`, {
          method: "PUT",
          body: JSON.stringify({ club_id: clubId, name: name.trim(), description: description || undefined, active }),
        });
        notify("Job-Typ aktualisiert");
      } else {
        await httpClient("/api/events/job-types", {
          method: "POST",
          body: JSON.stringify({ club_id: clubId, name: name.trim(), description: description || undefined, active }),
        });
        notify("Job-Typ erstellt");
      }
      onSaved();
      onClose();
    } catch (e: any) {
      notify(e?.body?.detail ?? "Fehler beim Speichern", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? "Job-Typ bearbeiten" : "Neuer Job-Typ"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
        <TextField
          label="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          fullWidth
          autoFocus
        />
        <TextField
          label="Beschreibung (optional)"
          value={description}
          onChange={e => setDescription(e.target.value)}
          multiline
          rows={2}
          fullWidth
        />
        <FormControlLabel
          control={<Switch checked={active} onChange={e => setActive(e.target.checked)} />}
          label="Aktiv"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button onClick={handleSave} variant="contained" disabled={!name.trim() || saving}>
          {saving ? <CircularProgress size={18} /> : "Speichern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function EventJobTypesPage() {
  const { clubId } = useClub();
  const notify = useNotify();
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<JobType | null>(null);

  const load = useCallback(async () => {
    if (!clubId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await httpClient(`/api/events/job-types?club_id=${clubId}`);
      setJobTypes(res.json?.data ?? []);
    } catch {
      notify("Fehler beim Laden", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [clubId, notify]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (jt: JobType) => {
    if (!window.confirm(`Job-Typ "${jt.name}" wirklich löschen?`)) return;
    try {
      await httpClient(`/api/events/job-types/${jt.id}`, { method: "DELETE" });
      notify("Job-Typ gelöscht");
      load();
    } catch {
      notify("Fehler beim Löschen", { type: "error" });
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight={700} display="flex" alignItems="center" gap={1}>
          <WorkIcon /> Job-Katalog
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditTarget(null); setDialogOpen(true); }}
        >
          Neuer Job
        </Button>
      </Box>

      {jobTypes.length === 0 ? (
        <Box textAlign="center" py={6}>
          <WorkIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography color="text.secondary">Noch keine Job-Typen definiert.</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Erstelle Jobs wie „Aufbau", „Abbau", „Schiedsrichter" usw.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {jobTypes.map(jt => (
            <Card key={jt.id} sx={{ borderLeft: "4px solid", borderLeftColor: jt.active ? "primary.main" : "divider" }}>
              <CardContent sx={{ pb: "12px !important" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography fontWeight={700}>{jt.name}</Typography>
                      {!jt.active && <Chip size="small" label="Inaktiv" variant="outlined" />}
                    </Box>
                    {jt.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {jt.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Bearbeiten">
                      <IconButton size="small" onClick={() => { setEditTarget(jt); setDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton size="small" color="error" onClick={() => handleDelete(jt)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <JobTypeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={editTarget}
        clubId={clubId!}
        onSaved={load}
      />
    </Box>
  );
}
